<template>
  <div class="pool-management-list">
    <b-row>
      <b-col cols="12" md="4" class="mb-4">
        <b-card body-class="pool-management-list__body">
          <h5 class="font-weight-bold mb-3">Talent Pools</h5>

          <div v-if="pools.length" class="pool-list">
            <button
              v-for="pool in pools"
              :key="pool.id"
              type="button"
              class="pool-list__item"
              :class="{ 'pool-list__item--active': selectedPoolId === pool.id }"
              @click="selectPool(pool.id)"
            >
              <span class="pool-list__name">{{ pool.name }}</span>
              <span class="pool-list__count">
                {{
                  pool.member_count != null
                    ? pool.member_count
                    : poolMembers(pool).length
                }}
              </span>
            </button>
          </div>

          <p v-else class="text-muted small mb-0">
            No pools yet - create one below.
          </p>

          <div class="pool-management-list__new">
            <span class="pool-management-list__new-label">New Pool</span>
            <b-form
              class="d-flex mt-2"
              style="gap: 0.5rem"
              @submit.prevent="onCreatePool"
            >
              <b-form-input
                v-model="newPoolName"
                size="sm"
                placeholder="Pool name"
                :disabled="!canManagePools"
              />
              <b-button
                type="submit"
                variant="primary"
                size="sm"
                :disabled="!newPoolName || !canManagePools || busy"
              >
                Create
              </b-button>
            </b-form>
          </div>
        </b-card>
      </b-col>

      <b-col cols="12" md="8">
        <b-card v-if="selectedPool" body-class="pool-management-list__body">
          <div class="pool-detail-header">
            <div
              v-if="!isRenaming"
              class="d-flex align-items-center"
              style="gap: 0.6rem"
            >
              <h5 class="font-weight-bold mb-0">{{ selectedPool.name }}</h5>
              <b-button
                v-if="canManagePools"
                variant="link"
                size="sm"
                class="p-0"
                @click="startRename"
              >
                Rename
              </b-button>
            </div>
            <b-form
              v-else
              class="d-flex align-items-center"
              style="gap: 0.5rem"
              @submit.prevent="onRenamePool"
            >
              <b-form-input v-model="renameValue" size="sm" autofocus />
              <b-button type="submit" size="sm" variant="primary"
                >Save</b-button
              >
              <b-button
                size="sm"
                variant="outline-secondary"
                @click="isRenaming = false"
              >
                Cancel
              </b-button>
            </b-form>

            <b-button
              v-if="canManagePools"
              variant="outline-danger"
              size="sm"
              @click="onDeletePool"
            >
              Delete Pool
            </b-button>
          </div>

          <p v-if="loadingDetail" class="text-muted small mb-0">
            Loading candidates…
          </p>

          <template v-else-if="poolMembers(selectedPool).length">
            <div class="pool-tag-filter mb-3">
              <div class="pool-tag-filter__row">
                <b-input-group size="sm" class="pool-tag-filter__search">
                  <b-input-group-prepend>
                    <b-input-group-text>
                      <b-icon-search />
                    </b-input-group-text>
                  </b-input-group-prepend>
                  <b-form-input
                    v-model="tagSearch"
                    placeholder="Search tags…"
                    autocomplete="off"
                  />
                </b-input-group>
                <button
                  v-if="tagSearch"
                  type="button"
                  class="pool-tag-filter__clear"
                  @click="tagSearch = ''"
                >
                  Clear search
                </button>
                <button
                  v-if="activeTags.length"
                  type="button"
                  class="pool-tag-filter__clear"
                  @click="clearAllTags"
                >
                  Clear tags ({{ activeTags.length }})
                </button>
              </div>

              <!-- Chips are every pool tag matching the search box, plus any
                   already-active tag (so selecting one and then typing a
                   different search term never strands it un-removable).
                   Clicking toggles membership in activeTags — multiple can be
                   active at once (AND: a candidate must carry all of them). -->
              <div v-if="tagSuggestions.length" class="pool-tag-filter__chips">
                <b-badge
                  v-for="tag in tagSuggestions"
                  :key="tag"
                  pill
                  role="button"
                  class="pool-tag-badge pool-tag-badge--filter"
                  :class="{ 'pool-tag-badge--active': isTagFilterActive(tag) }"
                  @click="toggleTagFilter(tag)"
                >
                  <b-icon-check-circle-fill
                    v-if="isTagFilterActive(tag)"
                    class="pool-tag-badge__check"
                  />
                  {{ tag }}
                </b-badge>
              </div>
              <p v-else-if="tagSearch" class="text-muted small mb-0 mt-2">
                No tags match "{{ tagSearch }}".
              </p>
            </div>

            <p v-if="!filteredMembers.length" class="text-muted small">
              No candidates match the selected filters.
            </p>

            <b-table
              v-else
              :items="filteredMembers"
              :fields="memberFields"
              class="pool-members-table"
            >
              <template #cell(name)="{ item }">
                <b-button
                  variant="link"
                  class="p-0 pool-members-table__name-link"
                  @click="goToCandidate(item)"
                >
                  {{ item.full_name || item.name || item.tradie_id }}
                </b-button>
              </template>
              <template #cell(tags)="{ item }">
                <div
                  v-if="editingTagsFor !== memberKey(item)"
                  class="d-flex align-items-center flex-wrap"
                  style="gap: 0.35rem"
                >
                  <b-badge
                    v-for="tag in item.tags || []"
                    :key="tag"
                    pill
                    class="pool-tag-badge"
                  >
                    {{ tag }}
                  </b-badge>
                  <span v-if="!(item.tags || []).length" class="text-muted"
                    >-</span
                  >
                  <b-button
                    v-if="canManagePools"
                    variant="link"
                    size="sm"
                    class="p-0"
                    @click="startEditTags(item)"
                  >
                    Edit
                  </b-button>
                </div>
                <div v-else style="min-width: 220px">
                  <b-form-input
                    v-model="tagsDraft"
                    size="sm"
                    autofocus
                    placeholder="e.g. carpenter, fifo"
                    class="mb-1"
                    @keyup.enter="saveTags(item)"
                  />
                  <small class="text-muted d-block mb-1"
                    >Comma-separated tags.</small
                  >
                  <div class="d-flex justify-content-end" style="gap: 0.35rem">
                    <b-button
                      size="sm"
                      variant="outline-secondary"
                      @click="cancelEditTags"
                    >
                      Cancel
                    </b-button>
                    <b-button
                      size="sm"
                      variant="primary"
                      :disabled="savingTags"
                      @click="saveTags(item)"
                    >
                      Save
                    </b-button>
                  </div>
                </div>
              </template>
              <template #cell(notes)="{ item }">
                <div
                  v-if="editingNoteFor !== memberKey(item)"
                  class="d-flex align-items-center"
                  style="gap: 0.5rem"
                >
                  <span class="text-truncate" style="max-width: 220px">
                    {{ item.notes || '-' }}
                  </span>
                  <b-button
                    v-if="canManagePools"
                    variant="link"
                    size="sm"
                    class="p-0"
                    @click="startEditNote(item)"
                  >
                    Edit
                  </b-button>
                </div>
                <div v-else style="min-width: 220px">
                  <b-form-textarea
                    v-model="noteDraft"
                    size="sm"
                    rows="2"
                    autofocus
                    class="mb-1"
                  />
                  <div class="d-flex justify-content-end" style="gap: 0.35rem">
                    <b-button
                      size="sm"
                      variant="outline-secondary"
                      @click="cancelEditNote"
                    >
                      Cancel
                    </b-button>
                    <b-button
                      size="sm"
                      variant="primary"
                      :disabled="savingNote"
                      @click="saveNote(item)"
                    >
                      Save
                    </b-button>
                  </div>
                </div>
              </template>
              <template #cell(actions)="{ item }">
                <b-button
                  v-if="canManagePools"
                  variant="outline-danger"
                  size="sm"
                  @click="onRemoveCandidate(item)"
                >
                  Remove
                </b-button>
              </template>
            </b-table>
          </template>
          <!-- GET /open-to-work/pools only returns {id, name, member_count}
               per the build spec — there's no endpoint that returns a
               pool's actual member list, so a pool with members > 0 can't
               show who they are yet. Say so honestly rather than claiming
               the pool is empty when member_count says otherwise. -->
          <div
            v-else-if="selectedPool.member_count > 0"
            class="pool-empty-state"
          >
            <p class="mb-0">
              {{ selectedPool.member_count }} candidate{{
                selectedPool.member_count === 1 ? '' : 's'
              }}
              in this pool - the member list isn't available from the API yet.
            </p>
          </div>
          <div v-else class="pool-empty-state">
            <p class="mb-0">No candidates in this pool yet.</p>
          </div>
        </b-card>

        <div v-else class="pool-empty-state pool-empty-state--card">
          <p class="mb-0">Select a pool to manage its candidates.</p>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'PoolManagementList',

  data() {
    return {
      selectedPoolId: null,
      newPoolName: '',
      isRenaming: false,
      renameValue: '',
      busy: false,
      editingNoteFor: null,
      noteDraft: '',
      savingNote: false,
      editingTagsFor: null,
      tagsDraft: '',
      savingTags: false,
      tagSearch: '',
      activeTags: [],
      loadingDetail: false,
      memberFields: [
        { key: 'name', label: 'Candidate' },
        { key: 'tags', label: 'Tags' },
        { key: 'notes', label: 'Notes' },
        { key: 'actions', label: '' },
      ],
    }
  },

  computed: {
    ...mapGetters('candidate', ['pools']),
    ...mapGetters('auth', ['hasPermission']),

    canManagePools() {
      return this.hasPermission('candidates.manage_pools')
    },

    selectedPool() {
      return this.pools.find((pool) => pool.id === this.selectedPoolId) || null
    },

    // All distinct tags currently in use across this pool's members —
    // rendered as quick-filter chips above the table.
    poolTags() {
      const tags = new Set()
      this.poolMembers(this.selectedPool).forEach((member) => {
        ;(member.tags || []).forEach((tag) => tags.add(tag))
      })
      return [...tags].sort((a, b) => a.localeCompare(b))
    },

    // Chip list: pool tags matching the search box, plus any tag already
    // active (so it stays visible/removable even once the search text no
    // longer matches it).
    tagSuggestions() {
      const needle = this.tagSearch.trim().toLowerCase()
      if (!needle) return this.poolTags
      return this.poolTags.filter(
        (tag) =>
          tag.toLowerCase().includes(needle) || this.activeTags.includes(tag),
      )
    },

    // Combines the multi-select chips (AND — a candidate must carry every
    // active tag, so picking a second tag narrows the result rather than
    // widening it) with the free-text search box (substring match against
    // any of the candidate's tags). Both apply together; neither overrides
    // the other.
    filteredMembers() {
      const members = this.poolMembers(this.selectedPool)
      const needle = this.tagSearch.trim().toLowerCase()
      return members.filter((member) => {
        const tags = member.tags || []
        const matchesActiveTags = this.activeTags.every((tag) =>
          tags.includes(tag),
        )
        if (!matchesActiveTags) return false
        if (!needle) return true
        return tags.some((tag) => tag.toLowerCase().includes(needle))
      })
    },
  },

  watch: {
    // A tag can vanish from the pool (last carrier had it edited off) while
    // still sitting in activeTags — drop it rather than silently filtering
    // to zero candidates against a tag nothing carries any more.
    poolTags(newTags) {
      const pruned = this.activeTags.filter((tag) => newTags.includes(tag))
      if (pruned.length !== this.activeTags.length) {
        this.activeTags = pruned
        this.syncRouteQuery()
      }
    },
  },

  created() {
    this.fetchPools().then(() => {
      // Restore the pool named in the URL (?pool=<id>) first — that's how a
      // "Back to Candidates" from a candidate profile lands the user back on
      // the exact pool they opened the candidate from. Otherwise fall back to
      // the first pool. Restore its tag filter (?tags=a,b) the same way, so
      // a page refresh doesn't lose it.
      const queryPoolId = this.$route.query.pool
      const queryPool =
        queryPoolId != null &&
        this.pools.find((pool) => String(pool.id) === String(queryPoolId))
      const initialTags = this.parseTagsQuery(this.$route.query.tags)
      if (queryPool) {
        this.selectPool(queryPool.id, initialTags)
      } else if (!this.selectedPoolId && this.pools.length) {
        this.selectPool(this.pools[0].id)
      }
    })
  },

  methods: {
    ...mapActions('candidate', [
      'fetchPools',
      'fetchPoolDetail',
      'createPool',
      'updatePool',
      'deletePool',
      'removeCandidateFromPool',
      'addPoolNote',
      'updatePoolTags',
    ]),

    poolMembers(pool) {
      return (pool && (pool.candidates || pool.members)) || []
    },

    async loadPoolDetail(poolId) {
      this.loadingDetail = true
      try {
        await this.fetchPoolDetail(poolId)
      } catch (err) {
        this.$emit('error', err)
      } finally {
        this.loadingDetail = false
      }
    },

    memberKey(member) {
      return member.tradie_id || member.id
    },

    startEditNote(member) {
      this.editingNoteFor = this.memberKey(member)
      this.noteDraft = member.notes || ''
    },

    cancelEditNote() {
      this.editingNoteFor = null
      this.noteDraft = ''
    },

    async saveNote(member) {
      if (!this.selectedPool || this.savingNote) return
      this.savingNote = true
      try {
        const poolId = this.selectedPool.id
        await this.addPoolNote({
          poolId,
          tradieId: this.memberKey(member),
          note: this.noteDraft,
        })
        await this.loadPoolDetail(poolId)
        this.cancelEditNote()
      } catch (err) {
        this.$emit('error', err)
      } finally {
        this.savingNote = false
      }
    },

    startEditTags(member) {
      this.editingTagsFor = this.memberKey(member)
      this.tagsDraft = (member.tags || []).join(', ')
    },

    cancelEditTags() {
      this.editingTagsFor = null
      this.tagsDraft = ''
    },

    async saveTags(member) {
      if (!this.selectedPool || this.savingTags) return
      this.savingTags = true
      try {
        const poolId = this.selectedPool.id
        const tags = this.tagsDraft
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean)
        await this.updatePoolTags({
          poolId,
          tradieId: this.memberKey(member),
          tags,
        })
        await this.loadPoolDetail(poolId)
        this.cancelEditTags()
      } catch (err) {
        this.$emit('error', err)
      } finally {
        this.savingTags = false
      }
    },

    // `initialTags` is only passed when restoring from the URL on load —
    // a normal user-driven pool switch always starts with a clean filter,
    // since tags from one pool's vocabulary rarely mean anything in another.
    selectPool(id, initialTags) {
      this.selectedPoolId = id
      this.isRenaming = false
      this.tagSearch = ''
      this.activeTags = initialTags ? [...initialTags] : []
      this.syncRouteQuery()
      this.loadPoolDetail(id)
    },

    parseTagsQuery(raw) {
      if (!raw) return []
      return String(raw)
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
    },

    // Mirrors the selected pool (?pool=<id>) and its active tag filter
    // (?tags=a,b) into the URL in one navigation. That's how a "Back to
    // Candidates" from a candidate profile returns to the exact pool (and
    // filter) it was opened from, and how a page refresh doesn't lose either.
    // Deliberately one replace() call rather than two separate ones (one per
    // param): vue-router's replace() is async and this.$route.query doesn't
    // update until it resolves, so two back-to-back calls would each read
    // the same stale query and race — the second overwriting the first's
    // change. replace(), not push(), so pool/filter changes don't stack
    // browser history.
    syncRouteQuery() {
      const query = { ...this.$route.query }

      if (this.selectedPoolId != null) {
        query.pool = String(this.selectedPoolId)
      } else {
        delete query.pool
      }

      const tagsValue = this.activeTags.join(',')
      if (tagsValue) {
        query.tags = tagsValue
      } else {
        delete query.tags
      }

      if (
        (this.$route.query.pool || '') === (query.pool || '') &&
        (this.$route.query.tags || '') === (query.tags || '')
      ) {
        return
      }
      this.$router.replace({ query }).catch(() => {})
    },

    goToCandidate(member) {
      this.$router.push({
        name: 'candidates.view-candidate',
        params: { candidateId: this.memberKey(member) },
      })
    },

    isTagFilterActive(tag) {
      return this.activeTags.includes(tag)
    },

    // Clicking a chip toggles that tag in/out of the multi-select filter —
    // any number can be active at once.
    toggleTagFilter(tag) {
      this.activeTags = this.isTagFilterActive(tag)
        ? this.activeTags.filter((active) => active !== tag)
        : [...this.activeTags, tag]
      this.syncRouteQuery()
    },

    clearAllTags() {
      this.activeTags = []
      this.syncRouteQuery()
    },

    async onCreatePool() {
      if (!this.newPoolName || this.busy) return
      this.busy = true
      try {
        await this.createPool(this.newPoolName)
        this.newPoolName = ''
      } catch (err) {
        this.$emit('error', err)
      } finally {
        this.busy = false
      }
    },

    startRename() {
      this.renameValue = this.selectedPool.name
      this.isRenaming = true
    },

    async onRenamePool() {
      if (!this.renameValue || !this.selectedPool) return
      this.busy = true
      try {
        await this.updatePool({
          id: this.selectedPool.id,
          name: this.renameValue,
        })
        this.isRenaming = false
      } catch (err) {
        this.$emit('error', err)
      } finally {
        this.busy = false
      }
    },

    async onDeletePool() {
      if (!this.selectedPool) return
      const ok = await this.$bvModal.msgBoxConfirm(
        `Delete pool "${this.selectedPool.name}"? This can't be undone.`,
        { title: 'Delete pool?', okVariant: 'danger', centered: true },
      )
      if (!ok) return
      try {
        await this.deletePool(this.selectedPool.id)
        if (this.pools.length) {
          // Goes through selectPool so the tag filter resets the same way
          // it does for any other pool switch.
          this.selectPool(this.pools[0].id)
        } else {
          this.selectedPoolId = null
          this.tagSearch = ''
          this.activeTags = []
          this.syncRouteQuery()
        }
      } catch (err) {
        this.$emit('error', err)
      }
    },

    async onRemoveCandidate(member) {
      if (!this.selectedPool) return
      const poolId = this.selectedPool.id
      try {
        await this.removeCandidateFromPool({
          poolId,
          tradieId: member.tradie_id || member.id,
        })
        await this.fetchPools()
        await this.loadPoolDetail(poolId)
      } catch (err) {
        this.$emit('error', err)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
/deep/ .pool-management-list__body {
  padding: 1.75rem;
}

.pool-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.pool-list__item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  border: 1px solid transparent;
  background: none;
  text-align: left;
  font-size: 0.9rem;
  color: #364052;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background-color: #f4f6f8;
  }

  &--active {
    background-color: #eaf3ff;
    border-color: #cfe0fb;
    color: #0057d9;
    font-weight: 600;
  }
}

.pool-list__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 0.75rem;
}

.pool-list__count {
  flex-shrink: 0;
  min-width: 1.5rem;
  text-align: center;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  background-color: #eceef1;
  color: #495057;
  font-size: 0.72rem;
  font-weight: 600;
}

.pool-list__item--active .pool-list__count {
  background-color: #d6e7ff;
  color: #0057d9;
}

.pool-management-list__new {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #e1e6ea;
}

.pool-management-list__new-label {
  font-weight: 700;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6c7581;
}

.pool-detail-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding-bottom: 1.1rem;
  margin-bottom: 1.25rem;
  border-bottom: 1px solid #e1e6ea;
}

.pool-tag-badge {
  background-color: #eceef1;
  color: #495057;
  font-weight: 600;
  font-size: 0.72rem;
  padding: 0.3rem 0.65rem;
}

.pool-tag-filter {
  padding: 0.85rem 1rem;
  background-color: #f8fafc;
  border: 1px solid #e5eaf2;
  border-radius: 10px;
}

.pool-tag-filter__row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.pool-tag-filter__search {
  max-width: 260px;

  ::v-deep .input-group-text {
    border-color: #dbe3ef;
    background: #fff;
    color: #8b95a3;
  }

  ::v-deep .form-control {
    border-color: #dbe3ef;
    box-shadow: none;

    &:focus {
      border-color: #0057d9;
    }
  }
}

.pool-tag-filter__clear {
  flex-shrink: 0;
  border: none;
  background: none;
  padding: 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: #6c7581;

  &:hover {
    color: #0057d9;
    text-decoration: underline;
  }
}

.pool-tag-filter__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.75rem;
}

.pool-tag-badge--filter {
  cursor: pointer;
  user-select: none;
  background-color: #fff;
  border: 1px solid #dde1e6;
  color: #495057;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background-color: #eaf3ff;
    border-color: #b8d4fb;
    color: #0057d9;
  }
}

.pool-tag-badge--active {
  background-color: #0057d9;
  border-color: #0057d9;
  color: #fff;

  &:hover {
    background-color: #0047b3;
    border-color: #0047b3;
    color: #fff;
  }
}

.pool-tag-badge__check {
  margin-right: 0.25rem;
  font-size: 0.68rem;
}

.pool-empty-state {
  padding: 2.5rem 1rem;
  text-align: center;
  color: #6c7581;

  &--card {
    background-color: #fff;
    border-radius: 10px;
    border: 1px solid #e1e6ea;
  }
}

.pool-members-table__name-link {
  font-weight: 600;
  color: #0057d9;
  text-align: left;
  white-space: normal;

  &:hover {
    text-decoration: underline;
  }
}

/deep/ .pool-members-table {
  margin-bottom: 0;

  th {
    border-top: none;
    border-bottom: 1px solid #e1e6ea;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6c7581;
    font-weight: 700;
    padding: 0.6rem 0.75rem;
  }

  td {
    vertical-align: middle;
    padding: 0.75rem;
    border-top: 1px solid #f0f2f4;
  }

  tbody tr:hover {
    background-color: #fafbfc;
  }
}
</style>
